import { EVENT } from "@/constants/event";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import { MATERIAL_DOWNLOAD_STATUS } from "@/constants/materialDownload";
import useAppStore from "@/context/store";
import { getBizDownloadDetail, getBizDownloadList } from "@/services";
import { TrackerEvent } from "@/tracker/shared/event";
import { PAGE_CHANGED } from "@/tracker/shared/variable";
import {
  downloadWithAIWatermarkCheck,
  getMaterialUrlAndFile,
} from "@/utils/download";
import { getOrgIdByUser } from "@/utils/gaoding";
import useTabShareState from "@/utils/hooks/useTabShareState";
import { MATERIAL_DOWNLOAD_RECORD_PAGE } from "@/utils/host";
import { gdImgSrcPolicy } from "@/utils/imageUrl";
import { openUdesk } from "@/utils/initUdesk";
import { useLocalStorageState, useMemoizedFn } from "ahooks";
import { Checkbox } from "antd";
import classNames from "classnames";
import { uniqBy } from "lodash";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import HBIcon from "../HBIcon";
import HBMessage from "../HBMessage";
import Image from "../Image";
import PinLink, { goToPinPage } from "../PinDetail/PinLink";
import styles from "./index.module.less";

export default function DownloadList() {
  const router = useRouter();
  const [{ isClose, autoClose, collapsed }, setState] = useLocalStorageState<{
    isClose?: boolean;
    autoClose?: boolean;
    collapsed?: boolean;
  }>(LOCAL_STORAGE_KEY.HB_DOWNLOAD_LIST, {
    defaultValue: {},
  });
  const setAutoClose = useMemoizedFn((autoClose: boolean) => {
    setState({ autoClose, isClose, collapsed });
  });
  const setClose = useMemoizedFn((isClose: boolean) => {
    setState({ autoClose, isClose, collapsed });
  });
  const setCollapsed = useMemoizedFn((collapsed: boolean) => {
    setState({ autoClose, isClose, collapsed });
  });

  // 全部下载完成标识
  const [finished, setFinished] = useState(false);
  const [list, setList] = useState<DownloadHistory[]>([]);
  const { user, vips } = useAppStore(({ user, vips }) => ({ user, vips }));
  // 未登录、不是vip不需要显示，下载列表为0
  const hideFlag = !user || !vips?.length || list.length === 0 || isClose;
  /**与上一次对比状态，判断是否需要触发下载 */
  const compareStatusAndDownload = useMemoizedFn(
    (curList: DownloadHistory[], nextList: DownloadHistory[]) => {
      // 比较前后是否有状态变化
      const downloadList = curList.filter(
        // 过滤出需要下载的
        (item) =>
          // 之前状态为pending
          item.download_status === MATERIAL_DOWNLOAD_STATUS.pending &&
          // 且当前状态为completed
          nextList.some(
            (i) =>
              i.id === item.id &&
              i.download_status === MATERIAL_DOWNLOAD_STATUS.completed
          )
      );

      // 同一个素材的下载，合并成一个下载
      uniqBy(downloadList, "material_id").forEach((item) => {
        download(item);
      });
    }
  );
  /**更新下载记录列表 */
  const updateList = useMemoizedFn(async () => {
    const res = await getBizDownloadList(
      {
        created_at_start: Math.floor(Date.now() / 1000 - 24 * 60 * 60),
      },
      {
        ignoreGdPermission: true,
        ignoreMessage: true,
      }
    );
    if (res && res.list) {
      compareStatusAndDownload(list, res.list);
      setList(res.list);
    }
  });

  const hasDownloadRef = useRef(false);
  const downloadList = useRef([]);
  // 多tab页共享下载记录，避免多tab页重复下载
  const [downloadedRecord, setDownloadedRecord] = useTabShareState(
    LOCAL_STORAGE_KEY.HB_DOWNLOADED_RECORD,
    {
      defaultValue: {},
    }
  );
  /** 判断是否全部下载完成。如果全部下载完成且勾选了自动关闭，3秒后自动关闭 */
  const isAllFinished = useMemoizedFn(() => {
    const isAllCompleted = list.every(
      (item) => item.download_status === MATERIAL_DOWNLOAD_STATUS.completed
    );

    // 全部下载完成后，标记为已完成
    if (isAllCompleted && !downloadList.current.length) {
      setFinished(true);

      // 3秒后自动关闭
      if (autoClose) {
        setTimeout(() => {
          setClose(true);
        }, 3000);
      }
    }
  });

  const addDownloadedRecord = useMemoizedFn((id: number) => {
    // 清除不在列表中的下载记录, 避免localStorage过大
    const listIds = list.map((item) => item.id);
    Object.keys(downloadedRecord).forEach((key) => {
      if (!listIds.includes(+key)) {
        delete downloadedRecord[key];
      }
    });

    setDownloadedRecord({ [id]: true, ...downloadedRecord });
  });

  /**
   * 自动下载
   * @param item 下载历史item
   * @param manual 是否手动触发
   */
  const download = useMemoizedFn(async function (
    item: DownloadHistory,
    manual = false
  ) {
    // 其他tab已经下载过了，不再下载
    if (downloadedRecord[item.id] && !manual) {
      return;
    }

    // 已经下载过一个了，放到缓存中，等待用户与页面交互后再下载，避免触发浏览器的多个文件下载的弹框拦截
    if (hasDownloadRef.current) {
      downloadList.current.push(item);
      return;
    }

    // 标记为已下载状态
    hasDownloadRef.current = true;

    let pinId = item.pin_id;

    if (pinId === "0") {
      const detail = await getBizDownloadDetail({ id: item.id });

      if (detail.pin_id) {
        pinId = detail.pin_id;
      } else {
        HBMessage.error("请前往稿定设计操作");
      }
    }

    const result = await getMaterialUrlAndFile(pinId, item.material_type);

    if (result?.url) {
      await downloadWithAIWatermarkCheck(result.url, result.pin);
      addDownloadedRecord(item.id);

      isAllFinished();
    }

    // 等待用户与页面交互后，下载缓存中的
    document.addEventListener(
      "click",
      () => {
        hasDownloadRef.current = false;
        if (downloadList.current.length) {
          const item = downloadList.current.shift();
          download(item);
        }
      },
      { once: true }
    );
  });

  const visibilityStateChangeCb = useMemoizedFn(() => {
    if (!hideFlag && document.visibilityState === "visible") {
      updateList();
    }
  });

  // 初始化动作： 1.更新下载列表 2.监听页面可见状态
  useEffect(() => {
    if (user && vips?.length) {
      !isClose && updateList();

      document.addEventListener("visibilitychange", visibilityStateChangeCb);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          visibilityStateChangeCb
        );
      };
    } else {
      setList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, vips, getOrgIdByUser(user)]);

  // 监听素材详情页的下载事件
  useEffect(() => {
    const onDownload = (e) => {
      const url = e.detail?.url;
      const pin = e.detail?.pin;
      if (url) {
        downloadWithAIWatermarkCheck(url, pin);
      }
      updateList();
      setFinished(false);
      setCollapsed(false);
      setClose(false);
    };

    window.addEventListener(EVENT.MATERIAL_DOWNLOAD, onDownload);

    //向下浏览 or 点击跳转页面  收成小窗
    const onScorllOrPageChange = () => {
      setCollapsed(true);
    };
    window.addEventListener("scroll", onScorllOrPageChange, {
      passive: true,
    });
    TrackerEvent.on(PAGE_CHANGED, onScorllOrPageChange);

    return () => {
      window.removeEventListener(EVENT.MATERIAL_DOWNLOAD, onDownload);
      window.removeEventListener("scroll", onScorllOrPageChange);
      TrackerEvent.off(PAGE_CHANGED, onScorllOrPageChange);
    };
  }, [isAllFinished, setClose, setCollapsed, updateList]);

  // 监听下载列表更新，自动下载
  useEffect(() => {
    // 下载列表为空，不需要处理
    if (list.length === 0) {
      return;
    }

    const hasPending = list.some(
      (item) => item.download_status === MATERIAL_DOWNLOAD_STATUS.pending
    );

    const hasFailure = list.some(
      (item) => item.download_status === MATERIAL_DOWNLOAD_STATUS.failure
    );

    if (hasPending || hasFailure) {
      setFinished(false);
    }

    // 有状态还在文件准备中的，2秒后更新列表，同时比较状态，判断是否需要下载
    if (hasPending && document.visibilityState === "visible") {
      const timer = setTimeout(() => {
        updateList();
      }, 2000);

      return () => {
        clearTimeout(timer);
      };
    }

    isAllFinished();
  }, [download, isAllFinished, list, updateList]);

  /**失败重试，跳转至素材详情页，唤起素材下载弹框 */
  const retry = useMemoizedFn((item: DownloadHistory) => {
    if (window.location.pathname === `/pins/${item.pin_id}`) {
      // 如果在当前页面，就刷新页面触发下载弹框
      document.getElementById("material_download_btn")?.click();
    } else {
      // 如果不在当前页面，就跳转到当前页面触发下载弹框
      // router.push(`/pins/${item.pin_id}?clickDownload=0`);
      // useAppStore.setState({ currentPin: null });
      goToPinPage({
        pinId: item.pin_id,
        urlParams: "clickDownload=0",
        router,
      });
    }
  });

  if (hideFlag) {
    return null;
  }
  /**已完成数量 */
  const finishedNum = list.filter(
    (item) => item.download_status === MATERIAL_DOWNLOAD_STATUS.completed
  ).length;

  return (
    <div
      className={classNames(styles.downloadList, {
        [styles.collapsed]: collapsed,
      })}
      onClick={() => collapsed && setCollapsed(false)}
    >
      <HBIcon
        className={styles.close}
        type="ic_clear2"
        onClick={(e) => {
          e.stopPropagation();
          setClose(true);
        }}
      />
      <div className={styles.head}>
        {finished && collapsed ? (
          <span>
            {/* <HBIcon className={styles.sucessIcon} type="ic_check_circle" /> */}
            已完成
            <span className={styles.num}>
              {finishedNum + "/" + list.length}
            </span>
          </span>
        ) : (
          <span>
            素材下载
            <span className={styles.num}>
              {finishedNum + "/" + list.length}
            </span>
          </span>
        )}
        <HBIcon
          className={styles.collapseIcon}
          type={collapsed ? "arrow_up" : "ic_arrow_down"}
          onClick={() => setCollapsed(!collapsed)}
        />
      </div>

      <div className={styles.list}>
        {list.map((item) => (
          <div className={styles.item} key={item.id}>
            <PinLink pinId={item.pin_id}>
              <div className={styles.preview}>
                <Image
                  {...gdImgSrcPolicy(item.material_preview.url, {
                    displayWidth: 60,
                  })}
                  alt={"素材预览"}
                  height={50}
                  width={50}
                />
              </div>
            </PinLink>
            <div className={styles.info}>
              <PinLink pinId={item.pin_id}>
                <div
                  className={styles.materialTitle}
                  title={item.material_title}
                >
                  {item.material_title}
                </div>
              </PinLink>
              <div className={styles.action}>
                <div className={styles.status}>
                  {item.download_status === MATERIAL_DOWNLOAD_STATUS.pending ? (
                    <span className={styles.downloading}>文件获取中</span>
                  ) : item.download_status ===
                    MATERIAL_DOWNLOAD_STATUS.failure ? (
                    <>
                      <span className={styles.failure}>文件获取失败</span>
                      <a onClick={() => retry(item)}>重试</a>或
                      <a onClick={openUdesk}>联系客服</a>
                    </>
                  ) : (
                    <>
                      <span className={styles.completed}>已完成</span>
                      <a onClick={() => download(item, true)}>再次下载</a>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.footer}>
        <span>
          <Checkbox
            className={styles.checkBox}
            checked={autoClose}
            onChange={(e) => {
              setAutoClose(e.target.checked);
            }}
          >
            完成后自动关闭
          </Checkbox>
        </span>
        <a target="_blank" href={MATERIAL_DOWNLOAD_RECORD_PAGE}>
          全部下载记录 <HBIcon type="ic_chevron_right" />
        </a>
      </div>
    </div>
  );
}
